import { LoadableResponse } from "api-types";
import { collection, onSnapshot, query } from "firebase/firestore";

import { useEffect, useState } from "react";

import { useFirebase } from "../firebase/useFirebase";

export type MetadataCollections =
  | "allergens"
  | "preferences"
  | "cuisines"
  | "allergen-mappings";

/**
 * Listen to an internal metadata collection (like allergens / preferences)
 *
 * A listener rather than a one-shot read so the persistent Firestore cache
 * serves the collection on mount and only changed documents are synced.
 */
export function useFirestoreMetadata<T>(
  language: string,
  metadataCollection: MetadataCollections
): LoadableResponse<T[]> {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<T[]>([]);
  const firebase = useFirebase();

  useEffect(() => {
    setLoading(true);
    return onSnapshot(
      query(
        collection(
          firebase.firestore,
          `metadata-language/${language}/${metadataCollection}`
        )
      ),
      (querySnapshot) => {
        setData(querySnapshot.docs.map((doc) => doc.data() as T));
        setLoading(false);
      },
      (error) => {
        console.error(
          `Error getting metadata ${metadataCollection} / ${language}`,
          error
        );
        setLoading(false);
      }
    );
  }, [firebase, language, metadataCollection]);

  return {
    isLoading: loading,
    data: data,
  };
}
